import { useEffect, useRef, useState } from 'react'
import { Layer, Rect, Stage, Transformer } from 'react-konva'
import type Konva from 'konva'
import type { KonvaEventObject } from 'konva/lib/Node'
import { useFloorPlanStore } from '../../store/useFloorPlanStore'
import { useFloorPlanMutations } from '../../hooks/useFloorPlanMutations'
import {
  getDimensionsForShape,
  GRID_SIZE,
  MIN_TABLE_SIZE
} from '../../utils/defaults'
import { FloorTable } from '../../types/floorPlan.model'
import {
  getTableOutline,
  outlinesOverlap,
  PlanArea,
  shiftIntoArea,
  TableOutline
} from '../../utils/seatLayout'
import TableNode from '../TableNode/TableNode'
import styles from './FloorPlanCanvas.module.scss'
import {
  primaryColors,
  whiteShades
} from '@/assets/styles/helpers/colors.styles'

// Anchor look mirrors SideBarDot: a solid white dot with a visible grey ring
// plus a soft white halo. The ring/halo darken default → hover → active using
// the white-shade palette. The border lines + rotate arrow are drawn in white.
const DOT_FILL = primaryColors.white // white dot centre
const HALO = primaryColors.white // soft white halo
const LINE_COLOR = primaryColors.white // border lines + rotate arrow (white)
const RING_DEFAULT = whiteShades.shade1 // light grey ring
const RING_HOVER = whiteShades.shade2 // darker grey on hover
const RING_ACTIVE = whiteShades.shade3 // darkest grey while pressed

// Every 15° around the circle. A half-step tolerance (7.5°) makes the whole
// range snap to the nearest increment instead of only near each exact angle.
const ROTATION_SNAPS = Array.from({ length: 24 }, (_, i) => i * 15)
const ROTATION_SNAP_TOLERANCE = 7.5

// Rotate handle rendered as the `las la-undo-alt` glyph in white. The glyph is
// drawn once onto a canvas and reused as the anchor's fill pattern image.
const ROTATE_GLYPH = '\uf2ea' // la-undo-alt
const ROTATE_ICON_SIZE = 24
// Negative because the rendered glyph has transparent padding inside its box;
// pulling the box down seats the visible arrow right above the connector line.
const ROTATE_ICON_GAP = -2
let rotateIconImage: HTMLImageElement | null = null
let rotateIconColor: string | null = null
let rotateIconRequested = false

function ensureRotateIcon(onReady: () => void) {
  // Re-draw whenever LINE_COLOR changes (e.g. after a hot reload) so the cached
  // glyph can never keep serving a stale colour.
  if (rotateIconImage && rotateIconColor === LINE_COLOR) {
    onReady()
    return
  }
  if (rotateIconRequested && rotateIconColor === LINE_COLOR) return
  rotateIconRequested = true
  rotateIconColor = LINE_COLOR

  const draw = () => {
    const px = 48
    const canvas = document.createElement('canvas')
    canvas.width = px
    canvas.height = px
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.fillStyle = LINE_COLOR
    ctx.font = `900 30px "Line Awesome Free"`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(ROTATE_GLYPH, px / 2, px / 2)
    const img = new Image()
    img.onload = () => {
      rotateIconImage = img
      onReady()
    }
    img.src = canvas.toDataURL()
  }

  if (document.fonts?.load) {
    document.fonts.load('900 30px "Line Awesome Free"').then(draw).catch(draw)
  } else {
    draw()
  }
}

/** Renders the rotate anchor as the white la-undo-alt icon (fill pattern). */
function applyRotateIcon(anchor: Konva.Rect) {
  if (!rotateIconImage) return
  const s = ROTATE_ICON_SIZE
  anchor.width(s)
  anchor.height(s)
  anchor.offsetX(s / 2)
  // Lift the icon above the anchor point so the connector line ends just below
  // it instead of running through it.
  anchor.offsetY(s + ROTATE_ICON_GAP)
  anchor.cornerRadius(0)
  anchor.strokeEnabled(false)
  anchor.shadowOpacity(0)
  anchor.fillPriority('pattern')
  anchor.fillPatternImage(rotateIconImage)
  anchor.fillPatternRepeat('no-repeat')
  anchor.fillPatternScale({
    x: s / rotateIconImage.width,
    y: s / rotateIconImage.height
  })
  anchor.fillPatternOffset({ x: 0, y: 0 })
}

/**
 * Styles each transformer handle like SideBarDot: a solid white dot with a
 * visible grey ring and a soft white halo. The ring + halo darken on hover and
 * darken further while the anchor is pressed (active resize/rotate).
 *
 * Colours are derived from the anchor's own hover/active attrs and re-applied
 * on every call, so Konva's default anchor styling can never persist.
 */
function styleAnchor(anchor: Konva.Rect) {
  // The rotate handle gets an icon instead of the dot once the glyph is ready.
  if (anchor.hasName('rotater') && rotateIconImage) {
    applyRotateIcon(anchor)
    return
  }

  const active = Boolean(anchor.getAttr('fpActive'))
  const hover = Boolean(anchor.getAttr('fpHover'))

  let ring = RING_DEFAULT
  let halo = 0.3
  if (active) {
    ring = RING_ACTIVE
    halo = 0.5
  } else if (hover) {
    ring = RING_HOVER
    halo = 0.4
  }

  anchor.cornerRadius(anchor.width() / 2) // circle
  anchor.fill(DOT_FILL)
  anchor.stroke(ring)
  anchor.strokeWidth(3)
  anchor.shadowColor(HALO)
  anchor.shadowBlur(6)
  anchor.shadowOffset({ x: 0, y: 0 })
  anchor.shadowOpacity(halo)
  anchor.shadowForStrokeEnabled(false)

  // Rebind hover/press listeners on every call (namespaced so the previous set
  // is removed first). Binding fresh each time means the handlers always close
  // over the current colours, a stale orange closure can't survive a reload.
  const repaint = () => {
    styleAnchor(anchor)
    anchor.getLayer()?.batchDraw()
  }
  anchor.off('.fp')
  anchor.on('mouseenter.fp', () => {
    anchor.setAttr('fpHover', true)
    repaint()
  })
  anchor.on('mouseleave.fp', () => {
    anchor.setAttr('fpHover', false)
    repaint()
  })
  anchor.on('mousedown.fp', () => {
    anchor.setAttr('fpActive', true)
    repaint()
    // The release often happens off the anchor (during drag/rotate), so the
    // anchor's own mouseup wouldn't fire, listen on window to always reset.
    const clearActive = () => {
      anchor.setAttr('fpActive', false)
      repaint()
      window.removeEventListener('mouseup', clearActive)
    }
    window.addEventListener('mouseup', clearActive)
  })
}

interface FloorPlanCanvasProps {
  selectedTableIds: string[]
  onSelect: (id: string, additive: boolean) => void
  onSetSelection: (ids: string[]) => void
  onClear: () => void
}

interface MarqueeRect {
  x: number
  y: number
  width: number
  height: number
}

function FloorPlanCanvas(props: FloorPlanCanvasProps) {
  const { selectedTableIds, onSelect, onSetSelection, onClear } = props

  const tables = useFloorPlanStore(s => s.tables)
  const activeRoomId = useFloorPlanStore(s => s.activeRoomId)
  const snapEnabled = useFloorPlanStore(s => s.snapEnabled)
  const zoom = useFloorPlanStore(s => s.zoom)
  const tool = useFloorPlanStore(s => s.tool)
  const { updateTablePosition, updateTablePositions, updateTableTransform } =
    useFloorPlanMutations()

  const wrapperRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<Konva.Stage>(null)
  const transformerRef = useRef<Konva.Transformer>(null)
  const nodeMap = useRef<Map<string, Konva.Group>>(new Map())

  // The Stage fills its container; size is measured so there are no scrollbars.
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 })

  // Marquee (drag-rectangle) multi-select state, in model coordinates.
  const [marquee, setMarquee] = useState<MarqueeRect | null>(null)
  const marqueeStartRef = useRef<{ x: number; y: number } | null>(null)

  // Pan offset (screen px), the stage position, in the store so the "centre on
  // tables" button can drive it. Dragging the background (hand tool) moves it.
  const pan = useFloorPlanStore(s => s.pan)
  const setPan = useFloorPlanStore(s => s.setPan)
  const panStartRef = useRef<{
    pointer: { x: number; y: number }
    pan: { x: number; y: number }
  } | null>(null)

  // Holding Space temporarily switches to the hand tool (Figma-style).
  const [spaceHeld, setSpaceHeld] = useState(false)
  const effectiveTool: 'select' | 'hand' = spaceHeld ? 'hand' : tool

  const registerNode = (id: string, node: Konva.Group | null) => {
    if (node) nodeMap.current.set(id, node)
    else nodeMap.current.delete(id)
  }

  const activeTables = tables.filter(table => table.roomId === activeRoomId)

  // True when `outline` (a table and its chairs) crosses that of a table in
  // the room outside `moving`.
  const overlapsOtherTable = (moving: Set<string>, outline: TableOutline) =>
    activeTables.some(
      other =>
        !moving.has(other.id) &&
        outlinesOverlap(
          outline,
          getTableOutline(other, {
            x: other.x + other.width / 2,
            y: other.y + other.height / 2
          })
        )
    )

  const collidesAt = (table: FloorTable, center: { x: number; y: number }) =>
    overlapsOtherTable(new Set([table.id]), getTableOutline(table, center))

  /*
    The part of the plan on screen. The room is what the owner sees: a table
    and its chairs are not dragged past its edge. Panning with the hand tool
    still reaches further.
  */
  const viewArea = (): PlanArea => ({
    left: -pan.x / zoom,
    top: -pan.y / zoom,
    right: (stageSize.width - pan.x) / zoom,
    bottom: (stageSize.height - pan.y) / zoom
  })

  const keepInView = (table: FloorTable, center: { x: number; y: number }) => {
    const shift = shiftIntoArea([getTableOutline(table, center)], viewArea())
    return { x: center.x + shift.x, y: center.y + shift.y }
  }

  // Group-drag: when one of several selected tables is dragged, the others move
  // by the same delta. We record every selected node's start position on the
  // first move and commit them all as one undo step on drop.
  const groupDragRef = useRef<{
    draggedId: string
    starts: Map<string, { x: number; y: number }>
    /** How far the group has been allowed to move from `starts`. */
    delta: { x: number; y: number }
  } | null>(null)

  const handleGroupDragStart = (id: string) => {
    // The Transformer starts a drag on every other selected node once the
    // grabbed one moves, and each fires its own dragstart. Those join the drag
    // already under way rather than restart it from where they now stand.
    const { current } = groupDragRef
    if (current && nodeMap.current.get(current.draggedId)?.isDragging()) return

    const starts = new Map<string, { x: number; y: number }>()
    selectedTableIds.forEach(selectedId => {
      const node = nodeMap.current.get(selectedId)
      if (node) starts.set(selectedId, { x: node.x(), y: node.y() })
    })
    groupDragRef.current = { draggedId: id, starts, delta: { x: 0, y: 0 } }
  }

  /** The selection's outlines with the group moved by (dx, dy). */
  const groupOutlines = (
    starts: Map<string, { x: number; y: number }>,
    dx: number,
    dy: number
  ) =>
    [...starts].flatMap(([id, start]) => {
      const table = activeTables.find(t => t.id === id)
      return table
        ? [getTableOutline(table, { x: start.x + dx, y: start.y + dy })]
        : []
    })

  /** True when the group, moved by (dx, dy), would land on a table outside it. */
  const groupCollides = (
    starts: Map<string, { x: number; y: number }>,
    dx: number,
    dy: number
  ) => {
    const moving = new Set(starts.keys())
    return groupOutlines(starts, dx, dy).some(outline =>
      overlapsOtherTable(moving, outline)
    )
  }

  /*
    Where a selected table may be dragged to, in model space. Konva drags every
    selected node on its own, each through its own bound, so if each checked
    for collision by itself the one that met a neighbour would stop and the rest
    walk on without it. Instead the grabbed table decides one move for the whole
    group, blocked when any of it would land on a table outside the selection
    and sliding along an axis that stays clear, as a single table does; the
    others only follow it. Konva bounds the grabbed node first on every move:
    it started dragging first.
  */
  const boundGroupDrag = (id: string, center: { x: number; y: number }) => {
    const group = groupDragRef.current
    const start = group?.starts.get(id)
    if (!group || !start) return center

    if (id === group.draggedId) {
      // Kept on screen as a whole, like a single table.
      const shift = shiftIntoArea(
        groupOutlines(group.starts, center.x - start.x, center.y - start.y),
        viewArea()
      )
      const dx = center.x - start.x + shift.x
      const dy = center.y - start.y + shift.y
      if (!groupCollides(group.starts, dx, dy)) {
        group.delta = { x: dx, y: dy }
      } else if (!groupCollides(group.starts, dx, group.delta.y)) {
        group.delta = { x: dx, y: group.delta.y }
      } else if (!groupCollides(group.starts, group.delta.x, dy)) {
        group.delta = { x: group.delta.x, y: dy }
      }
    }
    return { x: start.x + group.delta.x, y: start.y + group.delta.y }
  }

  const handleGroupDragMove = (id: string) => {
    const group = groupDragRef.current
    if (!group || group.draggedId !== id) return
    const dragged = nodeMap.current.get(id)
    const start = group.starts.get(id)
    if (!dragged || !start) return

    const dx = dragged.x() - start.x
    const dy = dragged.y() - start.y
    group.starts.forEach((pos, selectedId) => {
      if (selectedId === id) return
      const node = nodeMap.current.get(selectedId)
      if (node) node.position({ x: pos.x + dx, y: pos.y + dy })
    })
    // Recompute the selection box around the moved group, then repaint.
    transformerRef.current?.forceUpdate()
    dragged.getLayer()?.batchDraw()
  }

  const handleGroupDragEnd = () => {
    const group = groupDragRef.current
    groupDragRef.current = null
    if (!group) return

    const updates: { id: string; x: number; y: number }[] = []
    group.starts.forEach((_pos, selectedId) => {
      const node = nodeMap.current.get(selectedId)
      const table = tables.find(t => t.id === selectedId)
      if (!node || !table) return
      // Node origin is the table center; store the top-left.
      updates.push({
        id: selectedId,
        x: node.x() - table.width / 2,
        y: node.y() - table.height / 2
      })
    })
    updateTablePositions(updates)
  }

  // Track the container size and resize the Stage to match it.
  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return undefined

    const measure = () => {
      setStageSize({
        width: wrapper.clientWidth,
        height: wrapper.clientHeight
      })
      // Expose the canvas size so the store can place new tables on-canvas.
      useFloorPlanStore
        .getState()
        .setCanvasSize(wrapper.clientWidth, wrapper.clientHeight)
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(wrapper)
    return () => observer.disconnect()
  }, [])

  // Heal any table whose stored dimensions don't match its shape (e.g. a
  // 'rectangle' persisted as a 90x90 square by old code). Runs once on mount so
  // legacy data is corrected even if the persist migration didn't run.
  useEffect(() => {
    useFloorPlanStore.getState().tables.forEach(table => {
      const dims = getDimensionsForShape(table.shape, table.width, table.height)
      if (dims.width !== table.width || dims.height !== table.height) {
        updateTableTransform(table.id, dims)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Build the rotate-handle icon once, then restyle the transformer anchors.
  useEffect(() => {
    ensureRotateIcon(() => {
      const transformer = transformerRef.current
      transformer?.forceUpdate()
      transformer?.getLayer()?.batchDraw()
    })
  }, [])

  // Attach the transformer to every selected node imperatively.
  useEffect(() => {
    const transformer = transformerRef.current
    if (!transformer) return

    const nodes = selectedTableIds
      .map(id => nodeMap.current.get(id))
      .filter((node): node is Konva.Group => Boolean(node))

    transformer.nodes(nodes)
    transformer.getLayer()?.batchDraw()
  }, [selectedTableIds, activeTables])

  // Hold Space for a temporary hand tool (released on key up / blur).
  useEffect(() => {
    const isTyping = (target: EventTarget | null) => {
      const el = target as HTMLElement | null
      return (
        el?.tagName === 'INPUT' ||
        el?.tagName === 'TEXTAREA' ||
        Boolean(el?.isContentEditable)
      )
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code !== 'Space' || isTyping(event.target)) return
      event.preventDefault()
      setSpaceHeld(true)
    }
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code === 'Space') setSpaceHeld(false)
    }
    const onBlur = () => setSpaceHeld(false)
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', onBlur)
    }
  }, [])

  // Reflect the active tool in the canvas cursor (grab for the hand tool).
  useEffect(() => {
    const container = stageRef.current?.container()
    if (!container) return
    container.style.cursor = effectiveTool === 'hand' ? 'grab' : 'default'
  }, [effectiveTool, stageSize])

  // Hand tool: a press anywhere grabs and pans the board. Select tool: a press
  // on the empty background starts a marquee selection.
  const handleStageMouseDown = (
    e: KonvaEventObject<MouseEvent | TouchEvent>
  ) => {
    const stage = e.target.getStage()
    if (!stage) return

    if (effectiveTool === 'hand') {
      const pointer = stage.getPointerPosition()
      if (!pointer) return
      panStartRef.current = { pointer, pan }
      stage.container().style.cursor = 'grabbing'
      return
    }

    if (e.target !== stage) return
    const pos = stage.getRelativePointerPosition()
    if (!pos) return
    marqueeStartRef.current = pos
    setMarquee({ x: pos.x, y: pos.y, width: 0, height: 0 })
  }

  const handleStageMouseMove = (
    e: KonvaEventObject<MouseEvent | TouchEvent>
  ) => {
    const stage = e.target.getStage()
    if (!stage) return

    const marqueeStart = marqueeStartRef.current
    if (marqueeStart) {
      const pos = stage.getRelativePointerPosition()
      if (!pos) return
      setMarquee({
        x: Math.min(marqueeStart.x, pos.x),
        y: Math.min(marqueeStart.y, pos.y),
        width: Math.abs(pos.x - marqueeStart.x),
        height: Math.abs(pos.y - marqueeStart.y)
      })
      return
    }

    const panStart = panStartRef.current
    if (panStart) {
      const pointer = stage.getPointerPosition()
      if (!pointer) return
      const dx = pointer.x - panStart.pointer.x
      const dy = pointer.y - panStart.pointer.y
      setPan({ x: panStart.pan.x + dx, y: panStart.pan.y + dy })
    }
  }

  // Finalize a marquee selection, or end a pan (a pan that didn't move counts as
  // a plain click → clear the selection).
  const finishInteraction = (e: KonvaEventObject<MouseEvent | TouchEvent>) => {
    const container = e.target.getStage()?.container()
    if (container) {
      container.style.cursor = effectiveTool === 'hand' ? 'grab' : 'default'
    }

    if (marqueeStartRef.current) {
      const start = marqueeStartRef.current
      marqueeStartRef.current = null
      const rect = marquee
      setMarquee(null)
      if (!start || !rect || (rect.width < 4 && rect.height < 4)) {
        onClear()
        return
      }
      const ids = activeTables
        .filter(table => {
          const right = table.x + table.width
          const bottom = table.y + table.height
          return !(
            table.x > rect.x + rect.width ||
            right < rect.x ||
            table.y > rect.y + rect.height ||
            bottom < rect.y
          )
        })
        .map(table => table.id)
      onSetSelection(ids)
      return
    }

    // Hand-tool press: just end the pan (a click in hand mode does nothing).
    panStartRef.current = null
  }

  const hasSize = stageSize.width > 0 && stageSize.height > 0

  return (
    <div
      ref={wrapperRef}
      className={styles.canvasScroll}
      id="tour-floor-plan-canvas"
    >
      {hasSize && (
        <Stage
          ref={stageRef}
          width={stageSize.width}
          height={stageSize.height}
          scaleX={zoom}
          scaleY={zoom}
          x={pan.x}
          y={pan.y}
          onMouseDown={handleStageMouseDown}
          onTouchStart={handleStageMouseDown}
          onMouseMove={handleStageMouseMove}
          onTouchMove={handleStageMouseMove}
          onMouseUp={finishInteraction}
          onTouchEnd={finishInteraction}
          onMouseLeave={finishInteraction}
          className={styles.stage}
          // Keep the CSS grid in lockstep with the scaled + panned Konva content
          // so the grid lines stay aligned with the tables at every zoom/pan.
          style={{
            backgroundSize: `${GRID_SIZE * zoom}px ${GRID_SIZE * zoom}px, ${
              GRID_SIZE * zoom
            }px ${GRID_SIZE * zoom}px`,
            backgroundPosition: `${pan.x}px ${pan.y}px, ${pan.x}px ${pan.y}px`
          }}
        >
          <Layer>
            {activeTables.map(table => (
              <TableNode
                key={table.id}
                table={table}
                isSelected={selectedTableIds.includes(table.id)}
                isMultiSelected={
                  selectedTableIds.length > 1 &&
                  selectedTableIds.includes(table.id)
                }
                interactive={effectiveTool === 'select'}
                onSelect={additive => onSelect(table.id, additive)}
                registerNode={registerNode}
                collidesAt={center => collidesAt(table, center)}
                keepInView={center => keepInView(table, center)}
                boundGroupDrag={center => boundGroupDrag(table.id, center)}
                onGroupDragStart={() => handleGroupDragStart(table.id)}
                onGroupDragMove={() => handleGroupDragMove(table.id)}
                onGroupDragEnd={handleGroupDragEnd}
                onChange={patch => {
                  if (
                    patch.width !== undefined ||
                    patch.height !== undefined ||
                    patch.rotation !== undefined
                  ) {
                    updateTableTransform(table.id, patch)
                  } else if (patch.x !== undefined && patch.y !== undefined) {
                    updateTablePosition(table.id, patch.x, patch.y)
                  }
                }}
              />
            ))}
            <Transformer
              ref={transformerRef}
              // Resize/rotate only make sense for a single table; with several
              // selected we just show the bounding box.
              resizeEnabled={selectedTableIds.length === 1}
              rotateEnabled={selectedTableIds.length === 1}
              enabledAnchors={[
                'top-left',
                'top-right',
                'bottom-left',
                'bottom-right'
              ]}
              borderStroke={DOT_FILL}
              borderStrokeWidth={1.5}
              borderDash={[6, 4]}
              anchorSize={16}
              rotateAnchorOffset={20}
              rotationSnaps={snapEnabled ? ROTATION_SNAPS : []}
              rotationSnapTolerance={ROTATION_SNAP_TOLERANCE}
              anchorStyleFunc={styleAnchor}
              boundBoxFunc={(oldBox, newBox) => {
                if (
                  newBox.width < MIN_TABLE_SIZE ||
                  newBox.height < MIN_TABLE_SIZE
                ) {
                  return oldBox
                }
                return newBox
              }}
            />
            {marquee && (marquee.width > 0 || marquee.height > 0) && (
              <Rect
                x={marquee.x}
                y={marquee.y}
                width={marquee.width}
                height={marquee.height}
                fill="rgba(255, 101, 0, 0.12)"
                stroke={primaryColors.orange}
                strokeWidth={1 / zoom}
                dash={[4 / zoom, 4 / zoom]}
                listening={false}
              />
            )}
          </Layer>
        </Stage>
      )}
    </div>
  )
}

export default FloorPlanCanvas
